/* eslint-disable react/require-default-props */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import axios, { AxiosError } from 'axios';
import * as Sentry from '@sentry/react';
import Cookies from 'js-cookie';
// eslint-disable-next-line import/no-extraneous-dependencies
import { StatusCodes } from 'http-status-codes';
import { ERROR_MESSAGES } from '@common/constants';
import { languagesList } from '@common/language';
import { getErrorMessage, isOldToken } from '@common/utils';
import { InterviewType } from '@common/ts/enums';
import ArrowPathIcon from './src/components/icons/ArrowPathIcon';
import ChevronDoubleDownIcon from './src/components/icons/ChevronDoubleDownIcon';
import FlagIcon from './src/components/icons/FlagIcon';
import PlayIcon from './src/components/icons/PlayIcon';
import ChatInput from './src/components/input/ChatInput';
import TextMessage from './src/components/messages/text/TextMessage';
import { Button } from './src/components/shadcn/button';
import TooltipWrapper from './src/components/tooltip/TooltipWrapper';
import { cn } from './src/lib/utils';
import useChatStore from './src/store';
import 'react-toastify/dist/ReactToastify.css';
import { useVideoRecorder } from './src/hooks/useVideoRecording';
import useChatSettingsStore from './src/ChatSettingsStore';
import VideoPreview from './src/components/messages/video/VideoPreview';
import { ChatErrors } from './src/enums';
import { StandAloneChatProps, ProgressData } from './src/types';
import { usePermissionDialogs } from './src/hooks/usePermissionDialogs';
import { PermissionDialog } from './src/components/dialogs/PermissionDialog';
import { MediaTestDialog } from './src/components/dialogs/MediaTestDialog';
import {
  sendStatusChangeToParent,
  sendConversationCompleteToParent,
  sendDigestReadyToParent,
  isCompletionStatus,
  sendCodingReadyToParent,
  sendTelletProcessingToParent,
  sendTelletReadyToParent,
  sendCheckpointReachedToParent,
  onCheckpointComplete,
  isInIframe,
  ConversationStatus,
  type ConversationCompleteData,
  type DigestReadyData,
  type CodingReadyData,
} from './src/utils/iframeUtils';
import { iframeLocalStorage } from './src/utils/iframeLocalStorage';

const axiosInstanceChat = axios.create({
  baseURL: import.meta.env.VITE_CHAT_URL,
});

// TELV1-476: retry schedule for the transcript fetch behind
// CONVERSATION_COMPLETE. 4xx responses are terminal (retrying
// CONVERSATION_NOT_FOUND cannot succeed); network errors and 5xx get retried.
const IFRAME_DATA_RETRY_DELAYS_MS = [250, 1000];
// TELV1-470 — retry schedule for the completion-deciding progress fetch
const PROGRESS_RETRY_DELAYS_MS = [1000, 3000, 8000];

// TELV1-422 — max time the embed iframe shows its "processing" state before
// resolving to "done" (and firing tellet:ready with codingPending=true). Bounds
// how long a host that gates on tellet:ready can hold the respondent.
const ANALYSIS_DISPLAY_CAP_MS = 30_000;

function ChatPresentation({
  chatHeight,
  restartChatButton = false,
  handleRestartChat,
  isTestChat,
  isRefreshLoading,
  themeData,
  areScreeningQuestionsEnded,
  token,
  projectStorageSuffix,
  isProjectCompleted,
}: StandAloneChatProps) {
  const { chatSettingsData } = useChatSettingsStore();
  const { chat_id } = useParams();
  const [searchParams] = useSearchParams();
  const [languageCode, setLanguageCode] = useState<string>('en');

  // Get language from URL parameter or localStorage
  useEffect(() => {
    // First try to get from URL
    const urlLanguage = searchParams.get('language');
    if (urlLanguage) {
      const languageItem = languagesList.language.find(
        (lang) => lang.value.toLowerCase() === urlLanguage.toLowerCase(),
      );
      if (languageItem && languageItem.code) {
        setLanguageCode(languageItem.code);
        return;
      }
    }

    // Fallback to localStorage using the provided projectStorageSuffix
    if (projectStorageSuffix) {
      // Try to get language code from localStorage
      const storedLanguageCode = iframeLocalStorage.getItem(
        `current_project_language_code${projectStorageSuffix}`,
      );
      if (storedLanguageCode) {
        setLanguageCode(storedLanguageCode);
      }
    }
  }, [searchParams, projectStorageSuffix]);

  const {
    data: { messages },
    isLastMessageRerendered,
    currentConversationId,
    inputsDisabled,
    setInitialMessages,
  } = useChatStore((state) => state);
  const [showBackToBottom, setShowBackToBottom] = useState(false);
  const [isVoiceBoxOpen, setIsVoiceBoxOpen] = useState(false);
  const chatEnvironment = searchParams.get('environment');
  const isSandboxChat = chatEnvironment === 'sandbox';
  const chatBoxRef = useRef<HTMLDivElement>(null);
  const [progressData, setProgressData] = useState<ProgressData | null>(null);
  // F5 (TELV1-404): gates the standalone "interview complete" screen shown when
  // a finished interview is resumed but its history is missing (e.g. silent 401).
  const [showEmptyCompletion, setShowEmptyCompletion] = useState(false);
  const previousStatusRef = useRef<string | null>(null); // TE-1799: Track previous status for change detection
  const codingPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // TELV1-422 — standardised events + post-completion processing state.
  const processingFiredRef = useRef(false); // tellet:processing fired once (first DONE)
  const telletReadyFiredRef = useRef(false); // tellet:ready fired once
  // CONVERSATION_COMPLETE fired once — the existing completion block re-enters on
  // the DIGESTED transition (DONE→ANALYZING→DIGESTED; ANALYZING is not a
  // completion status), so without this guard the legacy event double-fires.
  const conversationCompleteFiredRef = useRef(false);
  const analysisCapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // TELV1-421 — checkpoint pause/resume.
  //   pausedAtMountOrdinalRef: which checkpoint ORDINAL the conversation was
  //     already paused at when this iframe mounted (or null). A reload by a
  //     returning respondent (host re-loads the same iframe; the storage bridge
  //     preserves the conversation) → auto-resume THAT segment. A pause reached
  //     LATER in this session (respondent just finished a segment) is a DIFFERENT
  //     ordinal → show the pause UI + hand back to the host. #2 — this must be
  //     per-ordinal, not a latching boolean: a boolean stayed true after the first
  //     auto-resume, so the 2nd+ checkpoint was mis-treated as mount-paused and
  //     silently dead-ended (no pause UI, no CHECKPOINT_REACHED, no resume).
  //   checkpointResumeFiredRef: ordinal the fresh-load auto-resume already fired
  //     for (set only on success so a transient failure re-attempts).
  //   checkpointReachedFiredRef: CHECKPOINT_REACHED fires once per ordinal.
  //   resumeInFlightRef: guards against overlapping resume calls.
  const checkpointFirstProgressRef = useRef(false);
  const pausedAtMountOrdinalRef = useRef<number | null>(null);
  const checkpointResumeFiredRef = useRef<number | null>(null);
  const checkpointReachedFiredRef = useRef<number | null>(null);
  const resumeInFlightRef = useRef(false);
  // Drives the iframe's "processing" loader (shown for coded embeds between DONE
  // and tellet:ready / cap). Never set on standalone (non-iframe) or non-coded.
  const [analysisProcessing, setAnalysisProcessing] = useState(false);
  // Preview checkpoint "Continue" in-flight guard (sandbox link / Test Chat).
  const [isResuming, setIsResuming] = useState(false);
  const navigate = useNavigate();
  const {
    previewStream,
    isVideoRecordingActive,
    showVideoRecordingModal,
    handleSubmitVideo,
    isVideoRecordingPressed,
    handleOpenVideoModal,
    isFrontCamera,
    videoPreviewUrl,
    handleStartVideoRecording,
    handleFlipCamera,
    statusVideo,
    handleStopVideoRecording,
    handlePauseVideoRecording,
    handleResumeVideoRecording,
    handleExitVideoRecording,
    capturing,
    setShowVoiceMainInput,
    handleCloseVideoModal,
    isVideoSending,
    isVideoStopping,
    isVideoPausing,
    isVideoExiting,
    isVideoStarting,
    isVideoResuming,
    isSwitchingCamera,
    getVideoConstraints,
  } = useVideoRecorder(projectStorageSuffix);

  // Store reference to voice recording trigger function
  const voiceRecordingTriggerRef = useRef<(() => void) | null>(null);

  // Handler for after media test completion
  const handleMediaTestComplete = (action: InterviewType.AUDIO | InterviewType.VIDEO) => {
    if (action === InterviewType.VIDEO) {
      handleOpenVideoModal();
    } else if (action === InterviewType.AUDIO) {
      // Trigger voice recording if the function is available
      if (voiceRecordingTriggerRef.current) {
        voiceRecordingTriggerRef.current();
      }
    }
  };

  // Callback to receive voice recording trigger function from ChatInput
  const handleVoiceRecordingTrigger = (startRecordingFn: () => void) => {
    voiceRecordingTriggerRef.current = startRecordingFn;
  };

  // Permission dialogs logic
  const {
    permissions,
    videoEnabled,
    audioEnabled,
    showPermissionModal,
    needsPermissionDialog,
    requestedPermissions,
    handleGrantAccess,
    handleCloseModal,
    requestCameraPermission,
    requestMicrophonePermission,
    mediaTest,
    isFirefoxAndroid,
    isGrantingAccess,
  } = usePermissionDialogs({
    chatSettingsData,
    isTestChat,
    onMediaTestComplete: handleMediaTestComplete,
    projectStorageSuffix,
  });

  // TELV1-470 — the progress fetch decides whether the Completed banner
  // shows and whether the input unmounts; it used to swallow every
  // transient failure with no retry, and nothing refetched after the
  // final message, so one lost request stranded a finished interview in
  // the "still open" state forever. Transient failures now retry with
  // backoff (mirroring the TELV1-476 iframe-data pattern); the handled
  // navigational 4xx cases keep their existing semantics and never retry.
  const fetchChatProgress = async () => {
    if (!(currentConversationId || areScreeningQuestionsEnded)) return;
    for (let attempt = 0; ; attempt += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await axiosInstanceChat.get(`/chat/progress/${currentConversationId}`);
        setProgressData(response.data);
        return;
      } catch (err) {
        const axiosError = err as AxiosError;
        const chatError = axiosError?.response?.status;
        const message = getErrorMessage(axiosError);

        // Chat not found because conversation does not exist
        const noConversationFound =
          chatError === StatusCodes.NOT_FOUND && message === ERROR_MESSAGES.CONVERSATION_NOT_FOUND;

        // This needs to be checked
        if (chatError === ChatErrors.CHAT_EXPIRED) {
          navigate(`/chat/error?error_status=${ChatErrors.CHAT_EXPIRED}`);
          return;
        }

        if (
          [ChatErrors.TEST_CONVERSATION_NOT_FOUND, ChatErrors.UNAUTHORIZED].includes(chatError!) ||
          noConversationFound
        ) {
          iframeLocalStorage.clear();
          window.location.reload();
          return;
        }

        const isTerminal = typeof chatError === 'number' && chatError >= 400 && chatError < 500;
        if (isTerminal || attempt >= PROGRESS_RETRY_DELAYS_MS.length) {
          console.error(err);
          return;
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          setTimeout(resolve, PROGRESS_RETRY_DELAYS_MS[attempt]);
        });
      }
    }
  };

  // TE-1799: Fetch complete iframe data for conversation complete event.
  // TELV1-476: transient failures are retried — a single lost fetch here used
  // to silently drop both CONVERSATION_COMPLETE and END, so the survey host
  // never received the transcript and never advanced.
  const fetchIframeData = useCallback(async (): Promise<ConversationCompleteData | null> => {
    if (!currentConversationId) return null;
    // The loop only exits via the returns below: success, terminal 4xx, or
    // the last configured attempt failing.
    for (let attempt = 0; ; attempt += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await axiosInstanceChat.get(`/chat/iframe-data/${currentConversationId}`);
        return response.data;
      } catch (err) {
        const httpStatus = (err as AxiosError)?.response?.status;
        const isTerminal = typeof httpStatus === 'number' && httpStatus >= 400 && httpStatus < 500;
        if (isTerminal || attempt >= IFRAME_DATA_RETRY_DELAYS_MS.length) {
          console.error('Failed to fetch iframe data:', err);
          // This axios instance has no interceptor and the error is swallowed
          // here, so without an explicit capture this failure class is
          // invisible in production.
          Sentry.captureException(err, {
            tags: {
              error_type: 'iframe_data_fetch_failed',
              conversation_id: currentConversationId,
            },
          });
          return null;
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          setTimeout(resolve, IFRAME_DATA_RETRY_DELAYS_MS[attempt]);
        });
      }
    }
  }, [currentConversationId]);

  const handleBackToBottom = () => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight + 64;
    }
  };

  // show scroll to bottom button
  const handleScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    if (
      e.currentTarget.scrollHeight - e.currentTarget.scrollTop - e.currentTarget.clientHeight <
      350
    ) {
      setShowBackToBottom(false);
    } else {
      setShowBackToBottom(true);
    }
  };

  useLayoutEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messages, isLastMessageRerendered]);

  useEffect(() => {
    handleBackToBottom();
  }, [isVoiceBoxOpen]);

  useEffect(() => {
    fetchChatProgress();
    if (!token) return;
    const isOldJwtToken = isOldToken(token);
    if (isOldJwtToken) Cookies.remove('token');
  }, [messages]);

  // TELV1-470 — additional refetch triggers. Every terminal SSE branch
  // re-enables the inputs, so the false-transition of inputsDisabled is a
  // reliable "the turn just ended" signal (cheaper and less invasive than
  // threading a stream-end callback out of useSendMessage). Tab
  // refocus/visibility covers respondents returning to a finished
  // interview whose final progress fetch was lost while backgrounded.
  useEffect(() => {
    if (!inputsDisabled) fetchChatProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputsDisabled]);

  useEffect(() => {
    const refetchOnReturn = () => {
      if (document.visibilityState === 'visible') fetchChatProgress();
    };
    document.addEventListener('visibilitychange', refetchOnReturn);
    window.addEventListener('focus', refetchOnReturn);
    return () => {
      document.removeEventListener('visibilitychange', refetchOnReturn);
      window.removeEventListener('focus', refetchOnReturn);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversationId, areScreeningQuestionsEnded]);

  const progressCount: number | undefined = progressData?.count;
  const progressLimit: number | undefined = progressData?.limit;
  const isProgressDone: boolean | undefined = progressData?.done;
  const percentage = progressCount && progressLimit ? (progressCount / progressLimit) * 100 : 0;

  // TELV1-421 — capture (once, on the first progress fetch) whether the
  // conversation was ALREADY paused at a checkpoint when this iframe mounted.
  // Mutating a ref during render to initialise-once is intentional.
  if (progressData && !checkpointFirstProgressRef.current) {
    checkpointFirstProgressRef.current = true;
    pausedAtMountOrdinalRef.current = progressData.checkpoint?.ordinal ?? null;
  }
  const checkpoint = progressData?.checkpoint ?? null;
  const isInProgress = progressData?.status === ConversationStatus.IN_PROGRESS;
  // Show the pause UI only for an IN-SESSION pause (the respondent just finished
  // a segment). A fresh load of an already-paused conversation (host reloaded the
  // iframe) auto-resumes instead. #2 — compare the CURRENT checkpoint ordinal to
  // the one we mounted paused at, so a later checkpoint reached in-session (a
  // different ordinal) correctly shows the pause UI even after an earlier
  // auto-resume.
  const isCheckpointPaused =
    !!checkpoint && isInProgress && checkpoint.ordinal !== pausedAtMountOrdinalRef.current;

  // Preview checkpoint navigation. The standalone sandbox link and the in-editor
  // Test Chat have NO host survey to drive the checkpoint handoff, so without
  // this the conversation dead-ends at the first checkpoint (isInProgress is
  // false, so the host-facing pause path above never fires). Offer an in-chat
  // marker + Continue that self-serves the SAME resumeFromCheckpoint() a host
  // triggers in production. `checkpoint` comes from getProgress (status-agnostic),
  // so no backend change is needed; a live host embed (isInProgress + isInIframe,
  // never isTestChat/isSandboxChat) is unaffected.
  const isPreview = Boolean(isTestChat || isSandboxChat);
  const isPreviewCheckpointPaused = isPreview && !!checkpoint;

  // Advance an embed conversation past its current checkpoint, then refresh the
  // message list + progress in place. Used both by the fresh-load auto-resume
  // (after the host reloads the iframe) and by an in-place CHECKPOINT_COMPLETE.
  // We refresh rather than reload so the resumed segment renders without bouncing
  // the iframe again; the reload that brings the respondent back is the host's.
  const resumeFromCheckpoint = async (): Promise<boolean> => {
    if (!currentConversationId) return false;
    // Don't overlap resume calls (a re-render + a CHECKPOINT_COMPLETE could
    // otherwise both fire); the backend advance is an idempotent CAS, but a
    // single in-flight request is cleaner.
    if (resumeInFlightRef.current) return false;
    resumeInFlightRef.current = true;
    try {
      await axiosInstanceChat.post(`/chat/resume/${currentConversationId}`);
      const { data } = await axiosInstanceChat.get(`/chat/history/${currentConversationId}`);
      if (data?.messages) setInitialMessages(data.messages);
      await fetchChatProgress();
      return true;
    } catch (error) {
      // Host-driven retry: do NOT mark the resume as done, so a reload or a
      // repeated CHECKPOINT_COMPLETE re-attempts. The backend advance is an
      // idempotent CAS, so retrying is safe and never double-advances.
      // eslint-disable-next-line no-console
      console.warn(
        '[ChatPresentation] resumeFromCheckpoint failed; will retry on next host signal',
        error,
      );
      return false;
    } finally {
      resumeInFlightRef.current = false;
    }
  };

  // Preview-only: self-serve the checkpoint handoff a host drives in production.
  // resumeFromCheckpoint refreshes messages + progress, so the next segment's
  // question renders and `checkpoint` clears (which hides the marker + restores
  // the input). Guarded by its own in-flight flag for the button's loading state.
  const handlePreviewContinue = async (): Promise<void> => {
    if (isResuming) return;
    setIsResuming(true);
    try {
      await resumeFromCheckpoint();
    } finally {
      setIsResuming(false);
    }
  };

  // TELV1-421 — fresh load of an already-paused conversation: the respondent has
  // returned (the host reloaded the same iframe; the storage bridge preserved the
  // conversation), so advance to the next segment automatically. The "done" ref
  // is set only on SUCCESS so a transient failure re-attempts on the next render
  // / reload (host-driven, idempotent via the backend CAS).
  useEffect(() => {
    if (!checkpoint || !isInProgress || !currentConversationId) return;
    // #2 — auto-resume ONLY the checkpoint we mounted paused at, and only once
    // per ordinal (so a transient failure re-attempts; a later in-session
    // checkpoint is NOT auto-resumed — it shows the pause UI instead).
    if (checkpoint.ordinal !== pausedAtMountOrdinalRef.current) return;
    if (checkpointResumeFiredRef.current === checkpoint.ordinal) return;
    resumeFromCheckpoint().then((ok) => {
      if (ok) checkpointResumeFiredRef.current = checkpoint.ordinal;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkpoint, isInProgress, currentConversationId]);

  // TELV1-421 — in-session pause: notify the host (CHECKPOINT_REACHED, once per
  // checkpoint) and accept an optional in-place CHECKPOINT_COMPLETE resume (for
  // a host that keeps the iframe mounted instead of reloading it).
  useEffect(() => {
    if (!isCheckpointPaused || !checkpoint || !isInIframe()) return undefined;
    if (checkpointReachedFiredRef.current !== checkpoint.ordinal) {
      checkpointReachedFiredRef.current = checkpoint.ordinal;
      sendCheckpointReachedToParent({
        conversationId: currentConversationId || '',
        timestamp: new Date().toISOString(),
        checkpointId: checkpoint.checkpoint_id,
        label: checkpoint.label,
        ordinal: checkpoint.ordinal,
      });
    }
    const unsubscribe = onCheckpointComplete(() => {
      resumeFromCheckpoint();
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckpointPaused, checkpoint?.ordinal, currentConversationId]);

  // TE-1799: Keep polling after conversation is done to detect digest status
  // Poll every 3 seconds for up to 90 seconds after conversation is done
  // to detect when status changes from 'done' to 'digested'
  useEffect(() => {
    // Only start polling when conversation is done but not yet digested.
    // TELV1-450 — PREVIEW_DIGESTED is the preview terminal; treat it like
    // DIGESTED so the poll stops once a preview run finishes.
    if (
      !isProgressDone ||
      progressData?.status === ConversationStatus.DIGESTED ||
      progressData?.status === ConversationStatus.PREVIEW_DIGESTED
    ) {
      return;
    }

    let pollCount = 0;
    const maxPolls = 30; // 30 polls * 3 seconds = 90 seconds

    const pollInterval = setInterval(() => {
      pollCount += 1;

      // Stop polling after 90 seconds (30 iterations)
      if (pollCount >= maxPolls) {
        clearInterval(pollInterval);
        return;
      }

      fetchChatProgress();
    }, 3000);

    // eslint-disable-next-line consistent-return
    return () => {
      clearInterval(pollInterval);
    };
  }, [isProgressDone, progressData?.status]);

  // F5 (TELV1-404): when a completed interview is resumed but its message
  // history could not be loaded (e.g. getChatHistory returns 401 and the error
  // is swallowed), the page would otherwise show only a bare "Completed" pill on
  // a blank screen. Surface a clear standalone completion screen instead — but
  // only after a short grace period, so it never flashes while history is still
  // loading on a normal resume. If messages arrive, the timer is cleared.
  useEffect(() => {
    if (isProgressDone && !messages?.length) {
      const timer = setTimeout(() => setShowEmptyCompletion(true), 1500);
      return () => clearTimeout(timer);
    }
    setShowEmptyCompletion(false);
    return undefined;
  }, [isProgressDone, messages?.length]);

  // TELV1-422 — fire tellet:ready exactly once, then stop the processing loader
  // and tear down the coding poll + cap timer so a trailing CODING_READY from an
  // in-flight poll can't land after ready. `codingPending` = the display cap
  // elapsed before coding finished (host reconciles server-side by respondent id).
  const fireTelletReady = (conversationId: string, codingPending: boolean) => {
    if (telletReadyFiredRef.current) return;
    telletReadyFiredRef.current = true;
    sendTelletReadyToParent({
      conversationId,
      timestamp: new Date().toISOString(),
      codingPending,
    });
    setAnalysisProcessing(false);
    if (codingPollIntervalRef.current) {
      clearInterval(codingPollIntervalRef.current);
      codingPollIntervalRef.current = null;
    }
    if (analysisCapTimerRef.current) {
      clearTimeout(analysisCapTimerRef.current);
      analysisCapTimerRef.current = null;
    }
  };

  // TELV1-422 — clear the analysis display-cap timer on unmount (it lives outside
  // the status-loop effect so the poll-effect churn doesn't reset it).
  useEffect(
    () => () => {
      if (analysisCapTimerRef.current) {
        clearTimeout(analysisCapTimerRef.current);
        analysisCapTimerRef.current = null;
      }
    },
    [],
  );

  // Per-segment checkpoint coding — emit CODING_READY incrementally. Tracks the
  // question ids already announced; whenever a NEW coded question appears (a
  // segment coded during a checkpoint pause, or the final post-completion pass)
  // it emits a CUMULATIVE payload — so hosts re-rendering from the latest event
  // are always correct, and hosts diffing get the new keys. Idempotent: the
  // pause poll, the DIGESTED branch, and the coding poll all call it safely.
  const sentCodedQuestionIdsRef = useRef<Set<string>>(new Set());
  const emitIncrementalCoding = (iframeData: ConversationCompleteData | null): void => {
    if (!iframeData?.coding) return;
    const codedIds = Object.keys(iframeData.coding).filter(
      (qid) => (iframeData.coding?.[qid]?.length ?? 0) > 0,
    );
    const hasNew = codedIds.some((qid) => !sentCodedQuestionIdsRef.current.has(qid));
    if (!hasNew) return;
    codedIds.forEach((qid) => sentCodedQuestionIdsRef.current.add(qid));
    const codingData: CodingReadyData = {
      conversationId: iframeData.conversationId,
      categories: iframeData.coding,
      complete: iframeData.codingComplete === true,
    };
    sendCodingReadyToParent(codingData);
  };

  // Per-segment checkpoint coding — while paused at an IN-SESSION checkpoint,
  // poll iframe-data briefly so the finished segment's codes (written by the
  // analyzer within seconds of the pause) reach the host as an incremental
  // CODING_READY DURING the pause — the window where it can branch its own
  // survey logic before resuming. Bounded: 12 × 2.5s ≈ 30s; stops early when
  // the project has no predefined categories (codingExpected false), when
  // coding is already complete, and on resume/unmount (effect cleanup).
  // NOTE (review N1): the fresh-load auto-resume path (mounted already paused →
  // isCheckpointPaused stays false) intentionally does NOT poll — the host has
  // already moved past its own survey block, so mid-pause branching is moot;
  // those codes arrive cumulatively with the post-completion emission.
  useEffect(() => {
    if (!isCheckpointPaused || !isInIframe() || !currentConversationId) return undefined;
    let attempts = 0;
    const maxAttempts = 12;
    const pausePollInterval = setInterval(async () => {
      attempts += 1;
      if (attempts > maxAttempts) {
        clearInterval(pausePollInterval);
        return;
      }
      const iframeData = await fetchIframeData();
      if (iframeData?.codingExpected === false) {
        clearInterval(pausePollInterval);
        return;
      }
      emitIncrementalCoding(iframeData);
      if (iframeData?.codingComplete) {
        clearInterval(pausePollInterval);
      }
    }, 2500);
    return () => clearInterval(pausePollInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckpointPaused, checkpoint?.ordinal, currentConversationId]);

  // TE-1799: Enhanced iframe messaging - status change detection and event sending
  useEffect(() => {
    const handleStatusChange = async () => {
      const currentStatus = progressData?.status;
      const previousStatus = previousStatusRef.current;

      // If status hasn't changed, do nothing
      if (currentStatus === previousStatus) return;

      // Update previous status reference
      previousStatusRef.current = currentStatus || null;

      // If we have a new status, send STATUS_CHANGE event
      if (currentStatus) {
        sendStatusChangeToParent({
          status: currentStatus as ConversationStatus,
          conversationId: currentConversationId || '',
          timestamp: new Date().toISOString(),
          progress: {
            count: progressData?.count || 0,
            limit: progressData?.limit || 0,
            done: progressData?.done || false,
          },
        });
      }

      // Handle completion statuses - send CONVERSATION_COMPLETE
      // TELV1-476: the completion set (see COMPLETION_STATUSES in iframeUtils)
      // mirrors the backend's done=true statuses. `analyzing` used to be
      // missing, so a session whose first observed status was `analyzing`
      // (the analyzer cron flips done→analyzing within 30s) ran the storage
      // cleanup below but never delivered the transcript to the host page.
      // Fetched at most once per status change; reused by the DIGEST_READY
      // branch when both fire on the same transition.
      let iframeData: ConversationCompleteData | null | undefined;

      if (isCompletionStatus(currentStatus)) {
        // Only send CONVERSATION_COMPLETE if we're transitioning to a completion status
        // (not if we're already at a completion status from a previous check)
        const wasAlreadyComplete = isCompletionStatus(previousStatus);

        if (!wasAlreadyComplete) {
          iframeData = await fetchIframeData();
          if (iframeData) {
            // TELV1-422 — dedupe CONVERSATION_COMPLETE within this page's
            // lifetime. Defensive since TELV1-476 put ANALYZING in the
            // completion set (the DONE→ANALYZING→DIGESTED chain now enters this
            // block once), but a status-poll race or a future change to the
            // completion set must not double-send the transcript to the host.
            if (!conversationCompleteFiredRef.current) {
              conversationCompleteFiredRef.current = true;
              sendConversationCompleteToParent(iframeData);
            }

            // TELV1-528 — persist the results token onto the HOST page
            // (INPUT bridge → attribute on this iframe's element) so a
            // one-question-per-page survey can navigate away and redeem
            // the results later. Outside the dedupe guard on purpose:
            // attribute writes are idempotent and a re-entered page
            // should re-persist. Suffix mirrors current_conversation_id.
            if (iframeData.results_token) {
              iframeLocalStorage.setItem(
                `results_token${projectStorageSuffix}`,
                iframeData.results_token,
              );
            }

            // TELV1-422 — standardised tellet:processing on the FIRST
            // completion transition. TELV1-533 B8 (decision 2026-08-18):
            // sandbox/test completions (SANDBOX_DONE/TEST_DONE) fire it too —
            // every generator snippet keys survey continuation on this event,
            // so the researcher's sanctioned sandbox test must behave like
            // production instead of looking stuck. Non-blocking: signals
            // analysis is running in the background; the respondent may
            // continue.
            if (
              (currentStatus === ConversationStatus.DONE ||
                currentStatus === ConversationStatus.SANDBOX_DONE ||
                currentStatus === ConversationStatus.TEST_DONE) &&
              !processingFiredRef.current &&
              isInIframe()
            ) {
              processingFiredRef.current = true;
              // Snapshot the id: `iframeData` is a mutable binding (the
              // DIGESTED branch below can refetch into it), so the deferred
              // display-cap closure must not read it.
              const processingConversationId = iframeData.conversationId;
              sendTelletProcessingToParent({
                conversationId: processingConversationId,
                timestamp: new Date().toISOString(),
              });
              // Show the processing loader + start the display cap ONLY when
              // coding is expected (a host may wait for tellet:ready). Non-coded
              // conversations have nothing to wait for → no loader; they become
              // ready at DIGESTED below.
              if (iframeData.codingExpected) {
                setAnalysisProcessing(true);
                analysisCapTimerRef.current = setTimeout(
                  () => fireTelletReady(processingConversationId, true),
                  ANALYSIS_DISPLAY_CAP_MS,
                );
              }
            }
          } else if (currentConversationId) {
            // TELV1-476: the transcript fetch failed permanently. The host must
            // still be notified: the degraded payload keeps the conversationId
            // (and the respondent linkage from the URL params) available for a
            // later server-side fetch, and the END that follows it must still
            // fire or the survey never advances and the respondent is stuck.
            // Dates are null (matching what the backend sends for unset
            // timestamps) and count is converted back to the raw question
            // index the real payload carries. Same dedupe ref as the success
            // path — a degraded transcript is still the one COMPLETE this
            // page gets to send.
            if (!conversationCompleteFiredRef.current) {
              conversationCompleteFiredRef.current = true;
              const degradedMetadata: Record<string, string> = {};
              // TELV1-533 A8 — respondent_id is the documented PRIMARY join
              // key; the degraded path must carry it or the host loses row
              // linkage exactly when the fetch failed.
              const respondentId = searchParams.get('respondent_id');
              const participantId = searchParams.get('participant_id');
              const urlLanguage = searchParams.get('language');
              if (respondentId) degradedMetadata.respondent_id = respondentId;
              if (participantId) degradedMetadata.participant_id = participantId;
              if (urlLanguage) degradedMetadata.language = urlLanguage;
              sendConversationCompleteToParent({
                conversationId: currentConversationId,
                status: currentStatus as ConversationStatus,
                metadata: degradedMetadata,
                messages: [],
                finishedAt: null,
                createdAt: null,
                startedAt: null,
                question_index: progressData?.count ? progressData.count - 1 : 0,
                // TELV1-297 fields are unknowable without the transcript fetch;
                // empty/null mirrors the degraded `messages`/date fields above.
                questions: [],
                respondentIndex: null,
                // TELV1-528 — the degraded path is exactly where the host most
                // needs a server-side handle: the progress poll (separate
                // request, own retry ladder) usually still has the token even
                // when iframe-data failed. Additive + nullable; the `error`
                // field below stays so hosts can distinguish degraded payloads.
                results_token: progressData?.results_token ?? null,
                error: 'Failed to load conversation data',
              });
              if (progressData?.results_token) {
                iframeLocalStorage.setItem(
                  `results_token${projectStorageSuffix}`,
                  progressData.results_token,
                );
              }
            }
          }
        }
      }

      // Handle DIGESTED status - send DIGEST_READY, then poll for CODING_READY.
      // DIGEST_READY / CODING_READY are iframe-only events, so skip the
      // iframe-data fetch and the coding poll entirely when not embedded —
      // standalone (interview) respondents would otherwise run 9 wasted
      // HTTP polls whose postMessages go nowhere.
      // TELV1-450 — fire DIGEST_READY/CODING_READY for preview runs too:
      // PREVIEW_DIGESTED is the preview terminal and must reach the host iframe
      // exactly like DIGESTED (that is the whole point of safe Preview). Live
      // (DIGESTED) behaviour is unchanged.
      const isDigestedStatus =
        currentStatus === ConversationStatus.DIGESTED ||
        currentStatus === ConversationStatus.PREVIEW_DIGESTED;
      const wasDigestedStatus =
        previousStatus === ConversationStatus.DIGESTED ||
        previousStatus === ConversationStatus.PREVIEW_DIGESTED;
      if (isDigestedStatus && !wasDigestedStatus && isInIframe()) {
        // Reuse a successful fetch from the completion branch, but if that
        // branch's fetch failed (null) or never ran, try fresh here — the
        // digest may still be deliverable even when the earlier attempt died.
        if (!iframeData) {
          iframeData = await fetchIframeData();
        }
        // #17 — tellet:processing fallback: the poll can jump in_progress →
        // digested (embed fast-digest is quick), so the DONE branch above may
        // never fire it. Emit it here before tellet:ready so a host that keys off
        // the recommended events always sees processing → ready in order.
        if (iframeData && !processingFiredRef.current && isInIframe()) {
          processingFiredRef.current = true;
          sendTelletProcessingToParent({
            conversationId: iframeData.conversationId,
            timestamp: new Date().toISOString(),
          });
        }
        if (iframeData?.digest) {
          const digestData: DigestReadyData = {
            conversationId: iframeData.conversationId,
            summary: iframeData.digest.summary,
            themes: iframeData.digest.themes,
            opportunities: iframeData.digest.opportunities,
            quotes: iframeData.digest.quotes,
          };
          sendDigestReadyToParent(digestData);
        }

        // Per-segment checkpoint coding — emit any not-yet-announced coding
        // (cumulative; segments coded at checkpoints may already have fired
        // mid-conversation), then gate tellet:ready on the COMPLETE state —
        // never on "any coding present": coding is written per question, so a
        // poll can land mid-pass and catch a partial snapshot (the bug that
        // shipped an incomplete CODING_READY as final).
        emitIncrementalCoding(iframeData);
        if (iframeData?.codingComplete) {
          // Every categorized question is coded → ready (host may release Next).
          fireTelletReady(iframeData.conversationId, false);
        } else if (iframeData && !iframeData.codingExpected) {
          // TELV1-422 — non-coded conversation: digest is the final analysis
          // step, so it's ready now — no coding wait, no processing loader.
          fireTelletReady(iframeData.conversationId, false);
        } else if (!telletReadyFiredRef.current) {
          // TELV1-422 — only poll while tellet:ready is still pending. If the
          // ~30s cap already fired ready (codingPending=true), don't burn poll
          // requests — the host reconciles the late coding by respondent id.
          // Poll for coding results — auto-categorization may still be running
          // Poll every 3 seconds, up to 10 attempts (~30 seconds) to cover the
          // worst-case digest-cron interval. Store ref so cleanup can clear it.
          if (codingPollIntervalRef.current) {
            clearInterval(codingPollIntervalRef.current);
          }
          let codingPollCount = 0;
          codingPollIntervalRef.current = setInterval(async () => {
            codingPollCount += 1;
            if (codingPollCount > 10) {
              if (codingPollIntervalRef.current) clearInterval(codingPollIntervalRef.current);
              codingPollIntervalRef.current = null;
              // TELV1-422 — complete coding didn't land within the poll window
              // → ready with codingPending so the host can reconcile
              // server-side later. (C5 invariant: this terminal fire is the
              // no-dead-end guarantee when codingComplete never becomes true,
              // e.g. a no-fit answer with no "Other" category defined.)
              if (iframeData?.conversationId) {
                fireTelletReady(iframeData.conversationId, true);
              }
              return;
            }

            try {
              const updatedData = await fetchIframeData();
              // Emit newly-coded questions as they land (incremental).
              emitIncrementalCoding(updatedData);
              if (updatedData?.codingComplete) {
                if (codingPollIntervalRef.current) clearInterval(codingPollIntervalRef.current);
                codingPollIntervalRef.current = null;
                // TELV1-422 — complete coded data arrived via poll → ready.
                fireTelletReady(updatedData.conversationId, false);
              }
            } catch {
              // Silent fail — coding is optional
            }
          }, 3000);
        }
      }

      // Legacy cleanup when progress is done
      if (isProgressDone) {
        const localStorageKeysToRemove = [
          `current_project_language_code${projectStorageSuffix}`,
          `current_project_language${projectStorageSuffix}`,
          `chat_settings${projectStorageSuffix}`,
          `screening_state${projectStorageSuffix}`,
        ];
        localStorageKeysToRemove.forEach((key) => {
          iframeLocalStorage.removeItem(key);
        });
      }
    };

    handleStatusChange();

    return () => {
      // Clean up coding poll interval on unmount or dependency change
      if (codingPollIntervalRef.current) {
        clearInterval(codingPollIntervalRef.current);
        codingPollIntervalRef.current = null;
      }
    };
  }, [
    progressData,
    isProgressDone,
    currentConversationId,
    chat_id,
    projectStorageSuffix,
    fetchIframeData,
    searchParams,
  ]);

  const isTextRightToLeft = ['ar', 'iw', 'fa', 'ur'].includes(languageCode);
  const lastAssistantMessage =
    messages && messages.length > 0 ? messages[messages.length - 1] : null;

  return (
    <div className="flex relative flex-col items-center w-full px-2 max-w-[1000px] mx-auto min500:px-0">
      {/* chat ui */}
      <div className={cn('relative w-full h-full')}>
        {/* flag */}
        {!isTestChat && isSandboxChat && (
          <div
            style={{
              backgroundColor: themeData?.['chat-primary'] || '#FF6F98',
            }}
            className="absolute left-0 right-0 z-50 flex items-center justify-center w-full text-xs font-medium text-white shadow-md min500:rounded-t-none min500:top-0 h-7 rounded-t-xl top-3 bg-chat-primary"
          >
            Test chat
          </div>
        )}

        {/* progress bar */}
        <div
          style={{
            backgroundColor: themeData?.['chat-progress-bar-bg'] || '#FF6F98',
          }}
          className={cn(
            'absolute left-0 right-0 min500:top-7 border-t-2 border-white overflow-hidden z-50 w-full h-6 top-10',
            !isSandboxChat && 'top-3 min500:rounded-t-none rounded-t-lg min500:top-0 border-none',
          )}
        >
          <div
            style={{
              width: `${percentage ?? 0}%`,
              backgroundColor: themeData?.primary || '#096846',
            }}
            className={cn(
              'flex w-full  justify-center items-center h-6 transition-all duration-700 ease-in-out rounded-r-full',
              // TELV1-421 — flatten the cap at full completion AND while paused at
              // a checkpoint, so the bar reads as "settled" for the finished
              // segment rather than mid-question. The fill % stays accurate.
              (percentage === 100 || isCheckpointPaused) && 'rounded-r-none',
            )}
          ></div>
        </div>
        {/* restart test chat */}
        {restartChatButton && (
          <TooltipWrapper text="Start conversation from the start." className="">
            <Button
              style={{ backgroundColor: themeData?.primary || '#096846' }}
              onClick={handleRestartChat}
              className="absolute z-50 transition-all border-2 border-white shadow-xl top-11 right-4"
              disabled={isProjectCompleted || isRefreshLoading}
            >
              {messages?.length === 0 ? (
                <PlayIcon className="size-5" />
              ) : (
                <ArrowPathIcon className={cn('size-5', isRefreshLoading && 'animate-spin')} />
              )}
            </Button>
          </TooltipWrapper>
        )}

        {/* restart sandbox chat */}
        {isSandboxChat && (
          <TooltipWrapper text="Start conversation from the start." className="">
            <Button
              style={{ backgroundColor: themeData?.primary || '#096846' }}
              onClick={handleRestartChat}
              className="absolute z-10 border-2 border-white shadow-xl top-[70px] right-4 transition-all"
            >
              {messages?.length === 0 ? (
                <PlayIcon className="size-5" />
              ) : (
                <ArrowPathIcon className="size-5" />
              )}
            </Button>
          </TooltipWrapper>
        )}
        <div
          ref={chatBoxRef}
          onScroll={handleScroll}
          className={`w-full mt-3 min500:mt-0 min500:rounded-none min500:border-none ${isVoiceBoxOpen && 'pb-[33vh]'} scroll-smooth relative min500:border-none min500:shadow-none border-2 rounded-xl shadow overflow-hidden overflow-y-auto
          ${isTestChat ? 'bg-gray-50' : 'bg-white'}
          ${chatHeight || ''}`}
        >
          <div className="w-full">
            {/* chat */}
            <div
              className={cn(
                'flex flex-col w-full h-full px-3 md700:px-1.5',
                isTestChat ? 'bg-gray-50' : 'bg-white',
              )}
            >
              {/* chat boxes */}
              <div className="flex flex-col items-center w-full h-full px-2">
                {/* messages */}
                <div
                  className={cn(
                    'flex flex-col w-full gap-5 mt-8 mb-3 h-fit',
                    isSandboxChat && 'mt-16',
                  )}
                >
                  {messages &&
                    messages.map((item, index) => (
                      <TextMessage
                        isTestChat={isTestChat}
                        key={item._id}
                        {...item}
                        isLastMessage={index === messages.length - 1}
                      />
                    ))}

                  {/* Preview checkpoint divider. Renders INSIDE the message flow
                      (not below the full-height chat card) as the last chat event,
                      so a researcher testing via the sandbox link / in-editor Test
                      Chat sees where their host survey takes over AND can step
                      through the whole flow without a real host. Raspberry accent =
                      Tellet Embed identity (#8E2F52, brand token); the Continue
                      button uses the primary green (themeData) because it's the
                      action. Never shown in a live host embed (isPreviewCheckpointPaused
                      is false there). Colours are inline hex — chatUI has no Tailwind
                      embed token; #C8688A is the WCAG-1.4.11-compliant border. */}
                  {isPreviewCheckpointPaused && (
                    <div className="w-full flex flex-col items-center gap-3 py-2 mb-2">
                      <div className="flex items-center gap-3 w-full">
                        <div
                          className="flex-1 border-t border-dashed"
                          style={{ borderColor: '#C8688A' }}
                        />
                        <span
                          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap"
                          style={{ backgroundColor: '#FBE4EC', color: '#8E2F52' }}
                        >
                          <FlagIcon className="h-3 w-3" />
                          Checkpoint{checkpoint?.label ? ` — ${checkpoint.label}` : ''}
                        </span>
                        <div
                          className="flex-1 border-t border-dashed"
                          style={{ borderColor: '#C8688A' }}
                        />
                      </div>
                      <p
                        className="text-xs max-w-xs text-center leading-relaxed"
                        style={{ color: '#6A5560' }}
                      >
                        In your live survey, respondents leave here to continue with your own
                        survey, then return.
                      </p>
                      <button
                        type="button"
                        onClick={handlePreviewContinue}
                        disabled={isResuming}
                        style={{ backgroundColor: themeData?.primary || '#096846' }}
                        className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-opacity disabled:opacity-60"
                      >
                        {isResuming ? (
                          <ArrowPathIcon className="h-4 w-4 animate-spin" />
                        ) : (
                          <PlayIcon className="h-4 w-4" />
                        )}
                        {isResuming ? 'Continuing…' : 'Continue to next section'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/*  back to bottom */}
      {showBackToBottom && (
        <div className="relative w-full">
          <button
            onClick={handleBackToBottom}
            type="button"
            style={{ backgroundColor: themeData?.primary || '#096846' }}
            className="absolute right-0.5 bottom-[calc(100%+100px)] rounded-l-xl text-white w-10 h-10 flex items-center justify-center"
          >
            <ChevronDoubleDownIcon className="size-5" />
          </button>
        </div>
      )}

      {showVideoRecordingModal && chatSettingsData?.includes('video') && (
        <VideoPreview
          handleSubmitVideo={handleSubmitVideo}
          stream={previewStream}
          themeData={themeData}
          isTestChat={isTestChat}
          isFrontCamera={isFrontCamera}
          videoPreviewUrl={videoPreviewUrl}
          handleStartVideoRecording={handleStartVideoRecording}
          handleFlipCamera={handleFlipCamera}
          statusVideo={statusVideo}
          handleStopVideoRecording={handleStopVideoRecording}
          handlePauseVideoRecording={handlePauseVideoRecording}
          handleResumeVideoRecording={handleResumeVideoRecording}
          handleExitVideoRecording={handleExitVideoRecording}
          capturing={capturing}
          previewStream={previewStream}
          handleCloseVideoModal={handleCloseVideoModal}
          setShowVoiceMainInput={setShowVoiceMainInput}
          isVideoSending={isVideoSending}
          isVideoStopping={isVideoStopping}
          isVideoPausing={isVideoPausing}
          isVideoExiting={isVideoExiting}
          isVideoStarting={isVideoStarting}
          isVideoResuming={isVideoResuming}
          isSwitchingCamera={isSwitchingCamera}
          getVideoConstraints={getVideoConstraints}
          languageCode={languageCode}
          message={{
            text: lastAssistantMessage?.text,
            hasMedia: Object.keys(lastAssistantMessage?.data || {}).length > 0,
          }}
        />
      )}

      {/* TELV1-421 — hide the input while paused at a checkpoint; the AI's
          closing turn (in the respondent's language) already tells them to
          continue with the survey, so no extra translatable copy is added. */}
      {!isProgressDone && !isCheckpointPaused && !isPreviewCheckpointPaused && (
        <div className="flex items-center w-full mb-2 md600:flex-col">
          <div className={cn('w-full', isTestChat && 'mt-4')}>
            <ChatInput
              isTestChat={isTestChat}
              setIsVoiceBoxOpen={setIsVoiceBoxOpen}
              isVoiceBoxOpen={isVoiceBoxOpen}
              themeData={themeData}
              isVideoRecordingActive={isVideoRecordingActive}
              isVideoRecordingPressed={isVideoRecordingPressed}
              handleOpenVideoModal={handleOpenVideoModal}
              requestCameraPermission={requestCameraPermission}
              requestMicrophonePermission={requestMicrophonePermission}
              permissions={permissions}
              languageCode={languageCode}
              onVoiceRecordingTrigger={handleVoiceRecordingTrigger}
              projectStorageSuffix={projectStorageSuffix}
            />
          </div>
        </div>
      )}

      {/* TELV1-421 — minimal, locale-neutral paused indicator. */}
      {isCheckpointPaused && (
        <div className="w-full min450:px-2 px-0 mt-5 flex items-center justify-center">
          <p
            className="text-md font-medium bg-white border-2 rounded-full shadow h-12 flex items-center justify-center px-6"
            aria-label="Section complete"
          >
            ✓
          </p>
        </div>
      )}

      {showEmptyCompletion ? (
        // F5 (TELV1-404): standalone completion screen for a resumed interview
        // whose history is unavailable — replaces a bare floating pill on a
        // blank page with a clearly-centered confirmation.
        <div className="w-full min450:px-2 px-0 mt-5 flex flex-col items-center justify-center text-center py-12">
          <p className="text-md font-medium bg-white border-2 rounded-full shadow min-h-12 flex items-center justify-center px-6 py-3">
            {progressData?.completedMessage || 'Completed 🥳'}
          </p>
        </div>
      ) : (
        <div
          className={cn(
            'w-full min450:px-2 px-0 pointer-events-none opacity-0 transition-opacity duration-300 ease-in mt-5',
            isProgressDone && (messages?.length ?? 0) > 0 && 'opacity-100',
          )}
        >
          <p
            className={cn(
              'text-md font-medium bg-white border-2 rounded-full shadow h-12 flex items-center justify-center',
            )}
          >
            {isInIframe() && analysisProcessing ? (
              // TELV1-422 — post-completion "processing" state for coded embeds,
              // shown until tellet:ready (coding done) or the ~30s display cap.
              <span className="flex items-center gap-2">
                <span
                  className="inline-block w-4 h-4 border-2 rounded-full border-current border-t-transparent animate-spin"
                  aria-hidden="true"
                />
                {progressData?.processingMessage || 'Processing… ⏳'}
              </span>
            ) : (
              progressData?.completedMessage || 'Completed 🥳'
            )}
          </p>
        </div>
      )}

      {/* Media Permissions Dialog */}
      <PermissionDialog
        isOpen={showPermissionModal}
        onClose={handleCloseModal}
        onGrantAccess={handleGrantAccess}
        requestedPermissions={requestedPermissions}
        permissions={permissions}
        themeData={themeData}
        needsPermissionDialog={needsPermissionDialog}
        videoEnabled={videoEnabled}
        audioEnabled={audioEnabled}
        isFirefoxAndroid={isFirefoxAndroid}
        languageCode={languageCode}
        isGrantingAccess={isGrantingAccess}
        isTextRightToLeft={isTextRightToLeft}
      />

      {/* Media Test Dialog */}
      <MediaTestDialog
        isOpen={mediaTest.showMediaTestModal}
        onClose={mediaTest.closeMediaTest}
        onContinue={mediaTest.handleTestComplete}
        testPermissions={mediaTest.testPermissions}
        themeData={themeData}
        languageCode={languageCode}
        isTextRightToLeft={isTextRightToLeft}
      />
    </div>
  );
}

export default ChatPresentation;
